import {useNavigate, useParams} from "react-router-dom";
import {validateTaiwanId} from "../../newApplicationStep3/model/validateTaiwanId";
import {validateTaiwanPhone} from "../../newApplicationStep3/model/validateTaiwanPhone";
import {validateDate} from "../../newApplicationStep3/model/validateDate";
import {objectToString} from "../../newApplicationStep3/model/objectToString";

interface Props {
  applicantName: string;
  setApplicantName: (value: string) => void;
  applicantOrganization: string;
  applicantTitle: string;
  applicantPhone: string;
  applicantPhonePrefix: string;
  patientName: string;
  patientId: string;
  patientGender: string;
  region: string;
  addressPart: string;
  yearRoc: string;
  month: string;
  day: string;
  patientHasTube: object;
  primaryCaregiverName: string;
  relationshipWithPatient: string;
  contactNumber: string;
  contactNumberPrefix: string;
  currentHomeCareService: object;
  description: string;
  setDialogContent: (value: string) => void;
  setMessage: (value: string) => void;
}

export const useCreateCase = ({
  applicantName,
  applicantOrganization,
  applicantTitle,
  applicantPhone,
  applicantPhonePrefix,
  patientName,
  patientId,
  patientGender,
  region,
  addressPart,
  yearRoc,
  month,
  day,
  patientHasTube,
  primaryCaregiverName,
  relationshipWithPatient,
  contactNumber,
  contactNumberPrefix,
  currentHomeCareService,
  description,
  setDialogContent,
  setMessage
}: Props) => {
  const {category} = useParams();
  const navigate = useNavigate();

  const handleCreateCase = async () => {
    try {
      // Step 1. validation
      const requiredFields = [
        {value: applicantName, message: '請填寫申請人姓名'},
        {value: applicantOrganization, message: '請填寫申請人服務單位'},
        {value: applicantTitle, message: '請填寫申請人職稱'},
        {value: applicantPhonePrefix, message: '請選擇申請人聯繫電話區碼'},
        {value: applicantPhone, message: '請填寫申請人聯繫電話'},
        {value: patientName, message: '請填寫個案姓名'},
        {value: patientGender, message: '請選擇個案性別'},
        {value: patientId, message: '請填寫個案身分證字號'},
        {value: region, message: '請選擇個案居住地區'},
        {value: addressPart, message: '請填寫個案居住地址'},
        {value: yearRoc && month && day, message: '請填寫個案出生年月日'},
        {value: primaryCaregiverName, message: '請填寫主要照顧者姓名'},
        {value: relationshipWithPatient, message: '請填寫與個案關係'},
        {value: contactNumberPrefix, message: '請填寫個案/家屬電話區碼'},
        {value: contactNumber, message: '請填寫個案/家屬電話'},
        {value: description, message: '請填寫個案現況簡述'},
      ];

      for (const field of requiredFields) {
        if (!field.value) {
          setDialogContent('message');
          setMessage(field.message);
          return;
        }
      }

      if (Object.values(patientHasTube).every(value => value === false || value === '')){
        setDialogContent('message');
        return setMessage('請選擇個案是否有管路');
      }

      if (Object.values(currentHomeCareService).every(value => value === false || value === '')){
        setDialogContent('message');
        return setMessage('請填寫目前接受在宅照服項目');
      }

      if (!validateTaiwanPhone(`${applicantPhonePrefix}-${applicantPhone}`)) {
        setDialogContent('message');
        return setMessage('請填寫正確的申請人聯繫電話');
      }

      if (!validateTaiwanId(patientId)) {
        setDialogContent('message');
        return setMessage('請填寫正確的身分證字號');
      }

      if (!validateDate({yearRoc, month, day})) {
        setDialogContent('message');
        return setMessage('請填寫正確的個案出生民國年月日');
      }

      if (!validateTaiwanPhone(`${contactNumberPrefix}-${contactNumber}`)) {
        setDialogContent('message');
        return setMessage('請填寫正確的個案/家屬電話');
      }

      // Step 2. Append new fields to the one stored in local storage
      const storedCase = localStorage.getItem('newCase') ? JSON.parse(localStorage.getItem('newCase')) : {};
      const newCase = {
        ...storedCase,
        applicantName,
        applicantOrganization,
        applicantTitle,
        applicantPhone: `${applicantPhonePrefix}-${applicantPhone}`,
        patientName,
        patientId,
        patientGender,
        patientRegion: region,
        patientAddress: `${region}${addressPart}`,
        patientBirthday: validateDate({yearRoc, month, day}),
        patientHasTube: objectToString(patientHasTube),
        primaryCaregiverName,
        relationshipWithPatient,
        contactNumber: `${contactNumberPrefix}-${contactNumber}`,
        currentHomeCareService: objectToString(currentHomeCareService),
        description,
        category,
      };

      localStorage.setItem('newCase', JSON.stringify(newCase));

      // Step 3. proceed to next step of the application
      navigate(`/newApplicationStep3/${category}`);
    } catch (error: any) {
      console.error('Error creating case:', error);
    }
  };
  return {handleCreateCase};
};
